/**
 * One invariant, enforced once: an API error's `detail` is a string, or it is
 * absent. It is never an object.
 *
 * WHY THIS EXISTS: FastAPI lets `HTTPException(detail=...)` carry any JSON, and
 * this backend uses that freely — `rate_limit_ai` raises
 * `{code, message, retry_after_seconds}`, the entitlement and subscription
 * gates raise their own shapes, and 422 validation raises an ARRAY. Meanwhile
 * ~57 call sites in this app were written the obvious way:
 *
 *     toast.error(err?.response?.data?.detail || 'Could not save')
 *
 * When `detail` arrived as an object that handed a bare object to React as a
 * child. React throws "Objects are not valid as a React child" DURING RENDER,
 * so the try/catch around the request could not catch it, and the page fell
 * through to ErrorBoundary — a full "Something went wrong" screen on what was
 * really a recoverable "wait a moment and try again".
 *
 * The fix is deliberately at the transport boundary rather than at the 57 call
 * sites. Patching the call sites would leave the NEXT one written the obvious
 * way just as broken; normalising here makes the obvious way the correct way,
 * which is the only version of this fix that stays fixed.
 *
 * The structured payload is not discarded — the billing and upgrade dialogs
 * branch on `detail.code`. It moves to `detailRaw`, so anything that wants the
 * object asks for the object explicitly.
 */

/** The structured error body some endpoints send in `detail`. */
export interface ApiErrorDetail {
  code?: string;
  message?: string;
  retry_after_seconds?: number;
  [key: string]: unknown;
}

/** A FastAPI 422 entry: `{loc, msg, type}`. */
interface ValidationEntry {
  msg?: unknown;
  loc?: unknown;
}

/** Best human-readable string for a raw `detail`, or undefined if there is none. */
function describe(detail: unknown): string | undefined {
  if (typeof detail === 'string') {
    return detail.trim() ? detail : undefined;
  }

  // FastAPI validation errors: [{loc, msg, type}, ...]
  if (Array.isArray(detail)) {
    const parts = (detail as ValidationEntry[])
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : null))
      .filter((m): m is string => !!m);
    return parts.length ? parts.join('; ') : undefined;
  }

  if (detail && typeof detail === 'object') {
    const d = detail as ApiErrorDetail;
    // `message` is this backend's convention; `detail` covers handlers that
    // nest a second detail inside the body (the FIR upload 422 does this).
    for (const key of ['message', 'detail'] as const) {
      const v = d[key];
      if (typeof v === 'string' && v.trim()) return v;
    }
    // A bare `{code: 'x'}` has nothing worth showing a lawyer. Returning
    // undefined lets the call site's own copy win, which is always better
    // written than anything we could synthesise from a code.
    return undefined;
  }

  return undefined;
}

/**
 * Rewrite an error response body in place so `detail` is safe to render.
 *
 * Mutates rather than returns a copy because it runs inside axios response
 * interceptors, where the body is already attached to the rejected error and
 * every existing call site reads it through `err.response.data`.
 */
export function normalizeErrorPayload(data: unknown): void {
  if (!data || typeof data !== 'object') return;

  const body = data as { detail?: unknown; detailRaw?: unknown };
  if (!('detail' in body)) return;

  const raw = body.detail;
  if (typeof raw === 'string' || raw === undefined) return; // already safe

  body.detailRaw = raw;
  body.detail = describe(raw);
}

/**
 * The message to show a user for a failed request. Always a string, so it can
 * never be the thing that crashes a render.
 *
 * Prefers, in order: the server's own words, the transport error, the caller's
 * fallback. Pass a fallback that reads like this specific action failing
 * ("Could not save the deadline"), not a generic one.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { detail?: unknown } };
    message?: unknown;
  } | undefined;

  const detail = err?.response?.data?.detail;
  const described = describe(detail);
  if (described) return described;

  // Network/timeout errors never reach the server, so they have no detail.
  const transport = err?.message;
  if (typeof transport === 'string' && transport.trim()) return transport;

  return fallback;
}

/** The structured payload, for the few call sites that branch on `detail.code`. */
export function apiErrorDetail(error: unknown): ApiErrorDetail | undefined {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  if (!data) return undefined;

  const raw = data.detailRaw ?? data.detail;
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as ApiErrorDetail)
    : undefined;
}
